import {makeAutoObservable, observable} from 'mobx'
import {Lang} from 'yougile-lang'
import {logger, ObjUtil, Throttle, Util} from 'yougile-util'
import {
  type SandboxTree,
  DataObjectId,
  Hub,
  HubUtil,
  RepeatStickerEntry,
  RepeatTemplate
} from 'yougile-model'
import {RepeatClient} from '@client/repeat-client'
import {Sync} from '@client/sync'
import {showConfirm} from '@components/common/base/notifications'
import {MessageBox} from '@components/message-box'
import {ChatUtils} from '@extensions/chat-utils'
import {Sticker, systemStickerCtl} from '@extensions/sticker/sticker'
import {createNewPhantom} from './create-new-phantom'
import {toggleTemplateState} from './toggle-template-state'

interface State {
  edited?: DataObjectId
  current?: RepeatTemplate
  editedFromId?: DataObjectId
  highlighted?: DataObjectId
  boardId?: DataObjectId
  scheduleEditFormValid: boolean
  templates: DataObjectId[]
  realTemplates: Hub[]
  hubHasChanged?: boolean
}

const EDIT_DEBOUNCE_MS = 200
const editThrottle = new Throttle(EDIT_DEBOUNCE_MS)

export class RepeatStore {
  state: State = {
    templates: [],
    scheduleEditFormValid: false,
    realTemplates: []
  }

  private preventClose = false
  private templates: Hub[] = []
  private isNewTemplate?: boolean
  private originId?: DataObjectId
  private initial?: string
  private initialHubUpdateString?: string
  private checkHubHasChangedTimer: NodeJS.Timeout | undefined

  constructor() {
    makeAutoObservable(this, {state: observable.shallow}, {autoBind: true})
  }

  get editedHub(): Hub | undefined {
    return this.state.edited
      ? this.templates?.find(h => h.id === this.state.edited)
      : undefined
  }

  get editedTpl(): RepeatTemplate | undefined {
    return this.editedHub?.data?.repeatTemplate
  }

  updateHubHasChanged(): void {
    if (this.state.edited) {
      this.state.hubHasChanged =
        this.initialHubUpdateString !== getHubUpdateString(this.editedHub)
      this.runTimer()
    }
  }

  get templateHasChanged(): boolean {
    return (
      this.isNewTemplate || this.initial !== JSON.stringify(this.state.current)
    )
  }

  get hasChanged(): boolean {
    return this.state.hubHasChanged || this.templateHasChanged
  }

  get canEdit(): boolean {
    return RepeatClient.canEditRepeatTasks(
      Sync.getCurrentSandbox(),
      this.state.boardId
    )
  }

  get canBeSaved(): boolean {
    return this.hasChanged && this.state.scheduleEditFormValid
  }

  setScheduleEditFormValid(val: boolean): void {
    this.state.scheduleEditFormValid = val
  }

  setBoardId(boardId?: DataObjectId): void {
    this.state.boardId = boardId
  }

  getBoard(): Hub | undefined {
    const box = Sync.getCurrentSandbox()
    return this.state.boardId ? box.getHub(this.state.boardId) : undefined
  }

  setHighlighted(highlighted?: DataObjectId): void {
    this.state.highlighted = highlighted
  }

  setEditedFromId(editedFromId?: DataObjectId): void {
    this.state.editedFromId = editedFromId
  }

  setCurrent(cur?: RepeatTemplate): void {
    if (!this.editedTpl || !cur) {
      return
    }

    if (!HubUtil.isPhantom(this.editedHub)) {
      logger.error('Can work with phantom only')
      return
    }

    editThrottle.run(() => this.updateTemplate(cur))
    this.state.current = cur
  }

  async cancelEditWithConfirm(): Promise<void> {
    if (
      this.editedTpl &&
      this.hasChanged &&
      !(await showConfirm(Lang.msg.repeat.confirmBack))
    ) {
      return
    }
    this.cancelEdit()
  }

  async close(): Promise<void> {
    if (this.preventClose) {
      this.preventClose = false
      return
    }

    if (this.editedTpl && this.hasChanged) {
      if (!(await showConfirm(Lang.msg.repeat.confirmClose))) {
        return
      }
    }

    this.cancelEdit()
    this.setBoardId(undefined)
  }

  create(hub?: Hub, editExisting?: boolean): boolean {
    const phantom = createNewPhantom({
      inBoardId: this.state.boardId,
      from: hub?.toJSON()
    })

    if (!phantom) {
      MessageBox.warn('Cannot create template')
      return false
    }

    this.loadTemplates()
    this.state.edited = phantom.id
    this.setCurrent(phantom.data.repeatTemplate)
    this.isNewTemplate = !editExisting
    this.originId = hub?.id
    if (editExisting) {
      this.initialHubUpdateString = getHubUpdateString(phantom)
      this.initial = JSON.stringify(phantom.data.repeatTemplate)
      this.runTimer()
    }

    return true
  }

  saveCurrent(): void {
    this.state.current && this.canBeSaved && this.save(this.state.current)
  }

  loadTemplates(): void {
    const templates = RepeatClient.getBoardTemplates(this.getBoard())
    this.templates = templates
    this.state.templates = templates.map(t => t.id)
    this.state.realTemplates = templates
      .filter(t => !HubUtil.isPhantom(t))
      .reverse()
  }

  openChat(): void {
    this.preventClose = true
    ChatUtils.toggleChat(this.editedHub, {tab: 'connected'})
  }

  toggleTemplateState(hub: Hub): void {
    if (!this.canEdit) {
      MessageBox.warn(Lang.msg.repeat.canEditWarn)
      return
    }
    toggleTemplateState(hub)
    this.state.realTemplates = [...this.state.realTemplates]
  }

  private updateTemplate(cur: RepeatTemplate): void {
    if (!this.editedTpl) {
      return
    }

    const updateRequired =
      this.editedTpl.title !== cur.title ||
      !ObjUtil.deepEqual(this.editedTpl.schedule, cur.schedule, false)

    if (updateRequired) {
      this.editedTpl.title = cur.title
      this.editedTpl.schedule = Util.clone(cur.schedule)
      this.editedHub?.commit()
    }
  }

  private save(newTpl: RepeatTemplate): void {
    if (!this.editedHub) {
      return
    }

    if (!HubUtil.isPhantom(this.editedHub)) {
      logger.error('Can work with phantom only')
      return
    }

    this.editedHub.data.repeatTemplate = newTpl
    const box = Sync.getCurrentSandbox()
    this.handleSave(box, newTpl)
    this.cleanup()
  }

  private handleSave(box: SandboxTree, newTpl: RepeatTemplate): void {
    box.removePhantom(this.editedHub)

    if (this.isNewTemplate) {
      this.editedHub.removeOnCommit()
      box.add(this.editedHub, this.editedHub.location, true, false)
    } else {
      const origin = box.get(this.originId) as Hub
      origin.data = {...this.editedHub.data}
      origin.stickers = {...this.editedHub.stickers}
      origin.properties = {...this.editedHub.properties}
      origin.description = this.editedHub.description
      origin.commit()
    }

    this.handleStickerAttachment(box, newTpl)
    this.setHighlighted(this.editedHub.id)
  }

  private handleStickerAttachment(
    box: SandboxTree,
    newTpl: RepeatTemplate
  ): void {
    if (!this.state.editedFromId) {
      return
    }

    const from = box.getHub(this.state.editedFromId)
    const sticker = systemStickerCtl.Repeat.getSticker(box)

    if (from && sticker) {
      const value: RepeatStickerEntry = {
        repeatedNumber: 0,
        templateId: newTpl.id
      }
      Sticker.pin(sticker, 'repeat', from, value)
    }
  }

  private cleanup(): void {
    this.state.edited = undefined
    this.initial = undefined
    this.setEditedFromId(undefined)
  }

  private cancelEdit(): void {
    const box = Sync.getCurrentSandbox()
    if (this.editedHub) {
      box.removePhantom(this.editedHub)
      this.editedHub.deleted = true
      this.editedHub.commit()
    }
    this.cleanup()
    this.setCurrent(undefined)
    this.setHighlighted(undefined)
    this.setScheduleEditFormValid(false)
  }

  private runTimer(): void {
    clearTimeout(this.checkHubHasChangedTimer)
    this.checkHubHasChangedTimer = setTimeout(this.updateHubHasChanged, 1000)
  }
}

export const repeatStore = new RepeatStore()

const getHubUpdateString = (hub: Hub): string => {
  return JSON.stringify({
    ...hub,
    id: undefined,
    data: {...hub?.data, repeatTemplate: undefined},
    updateTime: undefined,
    timestamp: undefined
  })
}
